/**
 * lib/modalFocus.ts — the three things every overlay in this codebase kept
 * forgetting, in one hook.
 *
 * A surface that covers the page and calls itself `role="dialog"` owes the
 * keyboard three behaviours, and the audit found overlays shipping with none
 * of them:
 *
 *   1. **Focus goes in.** Otherwise focus is still on the trigger BEHIND the
 *      overlay: a screen reader never hears the dialog (nothing announces a
 *      dialog nobody focused), and the first Tab walks the page underneath.
 *   2. **Focus stays in.** A trap written as a React `onKeyDown` on the
 *      overlay only ever sees keys that bubble from INSIDE it, so it can
 *      restore a trap but never establish one — which is worthless in exactly
 *      the case above. This binds the document in the capture phase.
 *   3. **Focus comes back.** Closing an overlay and dropping focus to `<body>`
 *      sends a keyboard user back to the top of the document, which on
 *      `/search` is roughly thirty tab stops from where they were.
 *
 * Escape is handled here too, for the same reason: bound on the panel it is a
 * no-op until focus is already inside, which is the state the user is least
 * likely to be in when they press it.
 *
 * It FAILS OPEN — if the container holds nothing focusable, Tab is left alone
 * rather than swallowed. A trap with nothing to focus is a lockout, and a
 * lockout is worse than a leak.
 */
import { useEffect, type RefObject } from 'react'

export const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/** Everything inside `el` a Tab could land on, in document order. */
export function focusablesIn(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

export interface ModalFocusOptions {
  /** Escape, from anywhere on the page while open. Omit to leave Escape alone. */
  onEscape?: () => void
  /**
   * Where focus lands on open. Default: the container itself (which must carry
   * `tabIndex={-1}`), so the dialog's own name and description are read before
   * its first control.
   */
  initialFocus?: () => HTMLElement | null | undefined
}

export function useModalFocus(
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  opts: ModalFocusOptions = {},
): void {
  const { onEscape, initialFocus } = opts

  // Move in, and put it back on the way out. One effect, so the element to
  // restore to is captured BEFORE anything steals focus.
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement
    // A frame late on purpose: the container is mounted by the same commit
    // that flipped `open`, and with AnimatePresence its ref can still be null
    // in this tick.
    const raf = requestAnimationFrame(() => {
      const el = containerRef.current
      if (!el) return
      if (el.contains(document.activeElement)) return
      const target = initialFocus?.() ?? el
      target?.focus({ preventScroll: true })
    })
    return () => {
      cancelAnimationFrame(raf)
      if (previous instanceof HTMLElement && previous.isConnected) {
        previous.focus({ preventScroll: true })
      }
    }
  }, [open, containerRef, initialFocus])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) { e.preventDefault(); onEscape(); return }
      if (e.key !== 'Tab') return
      const el = containerRef.current
      if (!el) return
      const nodes = focusablesIn(el)
      if (nodes.length === 0) return       // fail open — never swallow the key
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      if (!el.contains(active)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
        return
      }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, containerRef, onEscape])
}
